import {
  formatParisDateTime,
  RUN_STATUS,
  RUN_TYPE_LABELS,
  RUN_TYPE_SLUGS,
  type RunListItem,
  type RunListQuery,
} from '@flux/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Play } from 'lucide-react';
import type { JSX } from 'react';
import {
  createDataTableColumnHelper,
  DataTable,
  useDataTableUrlState,
} from '@/components/data-table/data-table.js';
import { Badge, StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import {
  fetchFulfillmentErrorLines,
  fetchRuns,
  runFulfillmentNow,
  runPennylaneNow,
} from './api.js';

const SORTABLE_COLUMNS = ['startedAt', 'status'] as const;
const NUMERIC_COLUMNS = ['processed', 'errors'] as const;
const PAGE_SIZE = 50;

const helper = createDataTableColumnHelper<RunListItem>();

const columns = helper.columns([
  helper.accessor('startedAt', {
    id: 'startedAt',
    header: 'Date',
    cell: (info) => (
      <span className="whitespace-nowrap tabular-nums">
        {formatParisDateTime(new Date(info.getValue()))}
      </span>
    ),
  }),
  helper.display({
    id: 'type',
    header: 'Type',
    cell: ({ row }) => (
      <span className="flex items-center gap-1.5">
        {RUN_TYPE_LABELS[row.original.type]}
        {row.original.simulation ? <Badge tone="orange">Simulation</Badge> : null}
      </span>
    ),
  }),
  helper.accessor('processed', { id: 'processed', header: 'Commandes traitées' }),
  helper.display({
    id: 'errors',
    header: 'Erreurs',
    cell: ({ row }) =>
      row.original.errors > 0 ? (
        <span className="font-medium text-status-red-fg">{row.original.errors}</span>
      ) : (
        <span className="text-muted-foreground">0</span>
      ),
  }),
  helper.accessor('status', {
    id: 'status',
    header: 'Statut',
    cell: (info) => <StatusBadge registry={RUN_STATUS} code={info.getValue()} />,
  }),
]);

/** Lien « Mises à jour Martelet non appliquées (N) », masqué quand il n'y en a aucune. */
function ErrorLinesLink(): JSX.Element | null {
  const params = { page: 1, pageSize: 1, sortDir: 'desc' as const };
  const query = useQuery({
    queryKey: queryKeys.runs.errorLines(params),
    queryFn: () => fetchFulfillmentErrorLines(params),
  });
  const total = query.data?.total ?? 0;
  if (total === 0) {
    return null;
  }

  return (
    <Link
      to="/journal/runs/lignes-en-erreur"
      className="inline-flex items-center gap-1.5 rounded-sm text-sm font-medium tabular-nums text-status-red-fg underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <AlertTriangle className="size-4" aria-hidden="true" />
      Mises à jour Martelet non appliquées ({total})
    </Link>
  );
}

/**
 * Onglet **Runs** du Journal (`02` §11) : Suivi Martelet et Pennylane dans une
 * seule table, plus les deux boutons de lancement manuel et le raccourci vers
 * les lignes en erreur.
 */
export function RunsTable(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { page, sortBy, sortDir, setPage, setSort } = useDataTableUrlState();

  const params: RunListQuery = {
    page,
    pageSize: PAGE_SIZE,
    sortDir,
    ...(sortBy ? { sortBy } : {}),
  };
  const query = useQuery({
    queryKey: queryKeys.runs.list(params),
    queryFn: () => fetchRuns(params),
  });

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: queryKeys.runs.all });
  }

  const fulfillmentRun = useMutation({
    mutationFn: () => runFulfillmentNow(false),
    onSuccess: () => {
      toast.success('Suivi Martelet lancé.');
      invalidate();
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  const pennylaneRun = useMutation({
    mutationFn: () => runPennylaneNow(false),
    onSuccess: () => {
      toast.success('Run Pennylane lancé.');
      invalidate();
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  return (
    <DataTable
      columns={columns}
      data={query.data?.items ?? []}
      getRowId={(row) => `${row.type}-${row.id}`}
      total={query.data?.total ?? 0}
      page={page}
      pageSize={PAGE_SIZE}
      sortBy={sortBy}
      sortDir={sortDir}
      sortableColumnIds={SORTABLE_COLUMNS}
      numericColumnIds={NUMERIC_COLUMNS}
      onPageChange={setPage}
      onSortChange={setSort}
      isLoading={query.isPending}
      isError={query.isError}
      {...(query.error instanceof ApiError ? { errorMessage: query.error.message } : {})}
      errorDetail={query.error instanceof ApiError ? query.error.detail : undefined}
      onRetry={() => {
        void query.refetch();
      }}
      onRowClick={(row) => {
        void navigate({ to: `/journal/runs/${RUN_TYPE_SLUGS[row.type]}/${row.id}` });
      }}
      toolbar={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <ErrorLinesLink />
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              isLoading={fulfillmentRun.isPending}
              onClick={() => {
                fulfillmentRun.mutate();
              }}
            >
              <Play aria-hidden="true" />
              Lancer le suivi Martelet
            </Button>
            <Button
              type="button"
              variant="outline"
              isLoading={pennylaneRun.isPending}
              onClick={() => {
                pennylaneRun.mutate();
              }}
            >
              <Play aria-hidden="true" />
              Lancer Pennylane
            </Button>
          </div>
        </div>
      }
      emptyState={{ title: 'Aucun run', description: 'Aucun run enregistré pour ces filtres.' }}
    />
  );
}
